import React, { useEffect, useState } from 'react';
import { getOrgBotProfile, updateOrgBotProfile, applyOrgBotProfile } from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import './BotProfilePanel.css';

const errorOf = (err, fallback) => err?.response?.data?.error || fallback;

const SECTIONS = [
  {
    title: 'Профиль бота',
    hint: 'Так бот выглядит в списке чатов и до нажатия «Старт». {org_name} заменится на название заведения.',
    fields: [
      { key: 'name', label: 'Имя бота' },
      { key: 'short_description', label: 'Короткое описание (в профиле бота)', rows: 2 },
      { key: 'description', label: 'Описание до нажатия «Старт»', rows: 7 },
    ],
  },
  {
    title: 'Что бот отвечает гостю',
    hint: 'В приветствии должно остаться согласие на рассылку и способ отписаться — этого требует закон о рекламе. {name} — имя гостя.',
    fields: [
      { key: 'greeting', label: 'Приветствие (до подписки)', rows: 6 },
      { key: 'linked', label: 'После подписки', rows: 4 },
      { key: 'welcome_bonus', label: 'Приветственный бонус начислен ({amount} — сумма)', rows: 3 },
      { key: 'help', label: 'Подсказка на непонятное сообщение', rows: 2 },
      { key: 'stopped', label: 'После отписки', rows: 2 },
    ],
  },
  {
    title: 'Контакты и ссылки',
    hint: 'Это видит гость по кнопкам «Контакты» и «Подарочный сертификат». Без ссылки на сертификаты кнопки не будет.',
    fields: [
      { key: 'address', label: 'Адрес' },
      { key: 'phone', label: 'Телефон' },
      { key: 'hours', label: 'Часы работы', placeholder: 'Например: ежедневно 10:00–23:00' },
      { key: 'map_url', label: 'Ссылка «Как добраться» (Яндекс Карты)' },
      { key: 'gift_url', label: 'Ссылка на подарочные сертификаты' },
    ],
  },
];

const CHANNEL_TITLES = { telegram: 'Telegram', max: 'MAX' };

const WEEKDAYS = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'];
const EMPTY_DAY = { kind: '', open: '', close: '' };

const parseSchedule = (raw) => {
  try {
    const days = JSON.parse(raw || '[]');
    if (Array.isArray(days) && days.length === 7) return days.map((d) => ({ ...EMPTY_DAY, ...d }));
  } catch (e) { /* пустой график */ }
  return WEEKDAYS.map(() => ({ ...EMPTY_DAY }));
};

// График для кнопки «🕐 График работы»: подпись дня и часы, пустые часы — выходной.
const ScheduleEditor = ({ value, disabled, onChange }) => {
  const days = parseSchedule(value);
  const setDay = (i, key, v) => onChange(JSON.stringify(days.map((d, j) => (j === i ? { ...d, [key]: v } : d))));
  return (
    <div className="botp-schedule">
      <datalist id="botp-day-kinds">
        <option value="Мужской день" />
        <option value="Женский день" />
        <option value="Семейный день" />
      </datalist>
      {days.map((d, i) => (
        <div key={WEEKDAYS[i]} className="botp-schedule-row">
          <span className="botp-schedule-day">{WEEKDAYS[i]}</span>
          <input type="text" list="botp-day-kinds" placeholder="Подпись, например «Мужской день»" value={d.kind}
                 maxLength={40} disabled={disabled} onChange={(e) => setDay(i, 'kind', e.target.value)} />
          <input type="time" value={d.open} disabled={disabled} aria-label={`${WEEKDAYS[i]}: открытие`}
                 onChange={(e) => setDay(i, 'open', e.target.value)} />
          <span className="botp-schedule-dash">–</span>
          <input type="time" value={d.close} disabled={disabled} aria-label={`${WEEKDAYS[i]}: закрытие`}
                 onChange={(e) => setDay(i, 'close', e.target.value)} />
        </div>
      ))}
    </div>
  );
};

const BotProfilePanel = ({ isAdmin }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState(null);
  const [applied, setApplied] = useState(null);

  const load = () => getOrgBotProfile().then((res) => { setData(res); setForm(res.profile); setDirty(false); });
  useEffect(() => { load().catch(() => setData({ error: true })); }, []);

  if (!data) return <LoadingSpinner />;
  if (data.error) return null;

  const set = (key, value) => { setForm((f) => ({ ...f, [key]: value })); setDirty(true); setMessage(null); };

  const save = async () => {
    setBusy('save'); setMessage(null);
    try {
      const res = await updateOrgBotProfile(form);
      setData((d) => ({ ...d, profile: res.profile }));
      setForm(res.profile);
      setDirty(false);
      setMessage({ ok: true, text: 'Сохранено. Новые тексты бот отвечает сразу; имя и описание — после «Применить».' });
      return true;
    } catch (err) {
      setMessage({ ok: false, text: errorOf(err, 'Не удалось сохранить') });
      return false;
    } finally {
      setBusy('');
    }
  };

  const apply = async () => {
    if (dirty && !(await save())) return;
    setBusy('apply'); setApplied(null);
    try {
      setApplied((await applyOrgBotProfile()).result);
    } catch (err) {
      setApplied({ error: errorOf(err, 'Не удалось применить') });
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="panel botp">
      <h4>Оформление бота</h4>
      <p className="ui-settings-hint">
        Один профиль для Telegram и MAX. Кнопки гостя: {data.buttons.join(' · ')}. Баланс бонусов бот берёт
        из iiko по номеру гостя — только для подписавшихся через Telegram или MAX.
      </p>

      {SECTIONS.map((section) => (
        <section key={section.title} className="botp-section">
          <div className="botp-section-title">{section.title}</div>
          <div className="ui-settings-hint">{section.hint}</div>
          {section.fields.map((f) => {
            const limit = data.limits[f.key];
            const value = form[f.key] || '';
            const Input = f.rows ? 'textarea' : 'input';
            return (
              <div key={f.key} className="ui-field">
                <label>
                  {f.label}
                  <span className={`botp-count ${value.length > limit ? 'over' : ''}`}>{value.length} / {limit}</span>
                </label>
                <Input rows={f.rows} value={value} disabled={!isAdmin} placeholder={f.placeholder || data.defaults[f.key]}
                       onChange={(e) => set(f.key, e.target.value)} />
              </div>
            );
          })}
        </section>
      ))}

      <section className="botp-section">
        <div className="botp-section-title">График работы</div>
        <div className="ui-settings-hint">
          Для кнопки «🕐 График работы»: сверху гость видит, как вы работаете сегодня и открыто ли сейчас, ниже — всю
          неделю. Пустые часы — выходной. Если график не заполнен, кнопки не будет.
        </div>
        <ScheduleEditor value={form.schedule} disabled={!isAdmin} onChange={(v) => set('schedule', v)} />
      </section>

      <section className="botp-section">
        <div className="botp-section-title">Аватар</div>
        {data.has_avatar ? (
          <div className="botp-avatar">
            <img src="/api/org/bot-avatar.png" alt="Аватар бота" />
            <div className="ui-settings-hint">
              И в Telegram, и в MAX аватар ставится вручную. Telegram: скачайте картинку, откройте
              {' '}<a href="https://t.me/BotFather" target="_blank" rel="noreferrer">@BotFather</a>, команда
              {' '}<code>/setuserpic</code>, выберите бота и отправьте картинку. MAX: кабинет
              {' '}<a href="https://business.max.ru" target="_blank" rel="noreferrer">business.max.ru</a> → «Чат-боты» →
              {' '}ваш бот → настройки: туда же вставьте имя и описание из полей выше.
              <div style={{ marginTop: 'var(--s-2)' }}>
                <a className="ui-btn-ghost" href="/api/org/bot-avatar.png" download>Скачать аватар</a>
              </div>
            </div>
          </div>
        ) : (
          <div className="ui-settings-hint">Логотипа организации нет — аватар загрузите в мессенджере вручную.</div>
        )}
      </section>

      {message && <div className={message.ok ? 'ui-inline-success' : 'ui-inline-error'}>{message.text}</div>}
      {applied?.error && <div className="ui-inline-error">{applied.error}</div>}
      {applied && !applied.error && Object.entries(applied).map(([channel, r]) => (
        <div key={channel} className={r.ok ? 'ui-inline-success' : 'ui-inline-error'}>
          {CHANNEL_TITLES[channel] || channel}: {r.ok
            ? (r.manual ? 'меню команд обновлено. Имя, описание и аватар MAX задаются вручную — см. ниже.' : 'оформление применено')
            : `ошибка — ${Object.values(r.errors).join('; ')}`}
        </div>
      ))}

      {isAdmin && (
        <div className="ui-form-actions botp-actions">
          <button type="button" className="ui-btn-ghost" onClick={save} disabled={!dirty || !!busy}>
            {busy === 'save' ? 'Сохраняем…' : 'Сохранить'}
          </button>
          <button type="button" className="ui-btn-primary" onClick={apply} disabled={!!busy}>
            {busy === 'apply' ? 'Применяем…' : 'Применить в Telegram и MAX'}
          </button>
        </div>
      )}
    </div>
  );
};

export default BotProfilePanel;
